import { Authenticated, Refine } from "@refinedev/core";
import routerProvider from "@refinedev/react-router";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router";

import { AppLayout } from "@/components/layout/AppLayout";
import { authProvider, dataProvider } from "@/lib/providers";
import { BarcodePage } from "@/pages/BarcodePage";
import { BoxesPage } from "@/pages/BoxesPage";
import { FbsBoxesPage } from "@/pages/FbsBoxesPage";
import { FbsBoxPage } from "@/pages/FbsBoxPage";
import { FbsControlPage } from "@/pages/FbsControlPage";
import { FbsTaskPage } from "@/pages/FbsTaskPage";
import { LoginPage } from "@/pages/LoginPage";
import { PlacementPage } from "@/pages/PlacementPage";
import { PlacePage } from "@/pages/PlacePage";
import { PodsortPage } from "@/pages/PodsortPage";
import { PodsortTasksPage } from "@/pages/PodsortTasksPage";
import { ProductPage } from "@/pages/ProductPage";
import { ProductsPage } from "@/pages/ProductsPage";
import { SerialPage } from "@/pages/SerialPage";
import { SpisaniePage } from "@/pages/SpisaniePage";
import { SpisanieSummaryPage } from "@/pages/SpisanieSummaryPage";

export function App() {
  return (
    <BrowserRouter>
      <Refine
        dataProvider={dataProvider}
        authProvider={authProvider}
        routerProvider={routerProvider}
        resources={[
          {
            name: "placement",
            list: "/placement",
            meta: { label: "Размещение" },
          },
          {
            // `show` объявлен рядом со `list`: товар открывается по модели, и
            // без этой строки Refine не знал бы, что у ресурса вообще есть
            // отдельная страница.
            name: "products",
            list: "/products",
            show: "/products/:id",
            meta: { label: "Товары" },
          },
          {
            // Баркод — это ОДИН РАЗМЕР товара, и адресуется он сам собой:
            // цифры с ярлыка и есть ключ. `list` ведёт на пустое поле поиска,
            // `show` — на разбор одного баркода.
            name: "barcode",
            list: "/barcode",
            show: "/barcode/:id",
            meta: { label: "Баркод" },
          },
          {
            // Короб опознаётся НОМЕРОМ МЕСТА (`placeID`), а не номером короба
            // на складе. Среди живых коробов номера сегодня не повторяются
            // (806 из 806, замер 08.09.26), но в группе «МП / Короб» целиком
            // повторы есть — номер 1 стоит у пяти мест. Адрес по номеру
            // однажды открыл бы чужой короб, а `placeID` — ключ CRM.
            name: "boxes",
            list: "/boxes",
            // `show` ведёт в раздел «Место»: карточка у короба общая со всем
            // складом с 11.09.26, своей у него больше нет.
            show: "/place/:id",
            meta: { label: "Короба" },
          },
          {
            // Короба ОТГРУЗКИ — соседний раздел «Коробам», но склад у него
            // другой: там короба хранения (группа 218), здесь конечная точка
            // сборки FBS (232 у ВБ, 234 у Ozon). Общего между ними только
            // слово «короб», поэтому и пункт меню свой.
            name: "fbs-boxes",
            list: "/fbs",
            show: "/fbs/:id",
            meta: { label: "FBS Короба" },
          },
          {
            // Складская задача FBS — единица СБОРКИ, общая на обе площадки, и
            // адресуется она НАШИМ номером (`fbs.fbs_task.id`), а не номером
            // задания ВБ или отправления Ozon: по чужому номеру пришлось бы
            // сначала знать, чей он. Свой путь `/fbs-task`, а не `/fbs/task`:
            // раздел «FBS Короба» адресуется номером МЕСТА, и «task» в этом
            // адресе выглядело бы как место с таким номером.
            name: "fbs-tasks",
            list: "/fbs-task",
            show: "/fbs-task/:id",
            meta: { label: "FBS Задача" },
          },
          {
            // Подсорт — раздел БЕЗ карточки: объект расчёта — БАРКОД, и своя
            // карточка у него уже есть («Баркод»). С 17.09.26 раздел
            // универсальный (ВБ и Ozon) и из двух экранов: расчёт и задачи.
            name: "podsort",
            list: "/podsort",
            meta: { label: "Подсорт" },
          },
          {
            name: "podsort-tasks",
            list: "/podsort/tasks",
            meta: { label: "Подсорт · Задачи" },
          },
          {
            // Списание — раздел БЕЗ карточки: объект здесь ОПЕРАЦИЯ, а её
            // разбор уводит в чужие карточки (баркод, серия, место). Своего
            // `show` у раздела поэтому нет — открывать по номеру списания
            // нечего.
            name: "spisanie",
            list: "/spisanie",
            meta: { label: "Списание" },
          },
          {
            // Итог к журналу списаний: сколько списано и оприходовано по
            // городам за период. Карточки нет — это сводка.
            name: "spisanie-summary",
            list: "/spisanie/summary",
            meta: { label: "Оприходование и списание" },
          },
          {
            // Контроль — раздел БЕЗ карточки: у него нет объекта, который
            // открывают по номеру. Объект здесь — сама сборка целиком, а
            // разбор находки уводит в чужие карточки (серия, задача), и
            // своего `show` у раздела поэтому нет.
            name: "fbs-control",
            list: "/fbs-control",
            meta: { label: "FBS Контроль" },
          },
        ]}
        options={{ disableTelemetry: true, warnWhenUnsavedChanges: false }}
      >
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            element={
              /* Ключ `fallback` ведёт на форму входа: не вошедший сотрудник
                 не должен видеть каркас кабинета даже мгновение. */
              <Authenticated
                key="app"
                fallback={<Navigate to="/login" replace />}
              >
                <AppLayout>
                  <Outlet />
                </AppLayout>
              </Authenticated>
            }
          >
            <Route index element={<Navigate to="/placement" replace />} />
            <Route path="/placement" element={<PlacementPage />} />
            <Route path="/products" element={<ProductsPage />} />
            {/* Товар опознаётся МОДЕЛЬЮ: у одной вещи столько карточек,
                сколько размеров, и адрес по карточке вёл бы на один размер
                вместо товара. */}
            <Route path="/products/:modelId" element={<ProductPage />} />
            {/* Серия опознаётся своим номером — тем, что напечатан на ярлыке
                вещи. Адрес без номера открывает тот же экран с пустым полем
                поиска: карточку серии зовут и из меню, когда номер ещё не
                набран. */}
            <Route path="/serial" element={<SerialPage />} />
            <Route path="/serial/:serial" element={<SerialPage />} />
            {/* Баркод — один размер товара. Адрес без баркода открывает тот же
                экран с пустым полем: раздел зовут и из меню, когда ярлык ещё
                не отсканирован. */}
            <Route path="/barcode" element={<BarcodePage />} />
            <Route path="/barcode/:barcode" element={<BarcodePage />} />
            {/* Короба: список — «какой везти первым», карточка — «почему
                именно его». Адрес карточки — номер МЕСТА, см. ресурс выше. */}
            <Route path="/boxes" element={<BoxesPage />} />
            {/* МЕСТО — одна карточка на любую точку склада: короб, стол
                подбора, зал, «Списано». Списка и пункта меню у раздела нет:
                сюда приходят из списка коробов и щелчком по концу перемещения
                в ленте. */}
            <Route path="/place/:placeId" element={<PlacePage />} />
            {/* Старый адрес карточки короба. Ведёт СЮДА ЖЕ, а не в никуда:
                ссылки на короба разосланы по перепискам и нарядам, и ломать их
                ради переезда экрана нельзя. */}
            <Route path="/boxes/:placeId" element={<BoxToPlace />} />
            {/* Короба отгрузки FBS: список — «что ещё за нами числится»,
                карточка — «какие именно вещи». Площадка живёт в адресе
                (`?platform=wb`), потому что ссылку отправляют сменщику. */}
            <Route path="/fbs" element={<FbsBoxesPage />} />
            <Route path="/fbs/:placeId" element={<FbsBoxPage />} />
            {/* Складская задача FBS. Сюда приходят из ленты движений — строка
                сказала «задача 1213 · упаковка», — поэтому адрес без номера
                открывает тот же экран с полем поиска, как у серии и баркода. */}
            <Route path="/fbs-task" element={<FbsTaskPage />} />
            <Route path="/fbs-task/:taskId" element={<FbsTaskPage />} />
            {/* Контроль сборки FBS: сходится ли учёт сам с собой. Адрес без
                номера и есть весь раздел — он про склад целиком, а не про
                одну вещь. */}
            <Route path="/fbs-control" element={<FbsControlPage />} />
            {/* Подсорт → Расчёт: чего не хватает витрине площадки и сколько
                собрать. Весь отбор — площадка, полка, город и норма — живёт в
                адресе (`?platform=`, `?shelf=`, `?city=`, `?norm=`): ссылку
                «что подсортить в Самаре» отправляют сменщику. */}
            <Route path="/podsort" element={<PodsortPage />} />
            {/* Подсорт → Задачи: что уже поставлено в работу сборщику. */}
            <Route path="/podsort/tasks" element={<PodsortTasksPage />} />
            {/* Старый адрес раздела «Подсорт FBW». Ведёт СЮДА ЖЕ с тем же
                отбором: ссылки разосланы (`/fbw?city=7`), и площадки в них нет
                — значит ВБ, ровно то, что они показывали. */}
            <Route path="/fbw" element={<FbwToPodsort />} />
            {/* Списание: что склад списал и откуда. Причина и потолок живут в
                адресе (`?category=`, `?n=`) — ссылку на находку отправляют
                сменщику, и открыться она обязана такой же. */}
            <Route path="/spisanie" element={<SpisaniePage />} />
            {/* Оприходование и списание по городам за период (`?from=`,
                `?to=`), без потока маркетплейса; выбранная цифра — тоже в
                адресе (`?side=`, `?city=`, `?category=`). */}
            <Route path="/spisanie/summary" element={<SpisanieSummaryPage />} />
            {/* Прежний адрес раздела (до 29.09.26) — сюда же с тем же периодом:
                ссылки из разметки и переписки остаются рабочими. */}
            <Route path="/balance" element={<BalanceToSummary />} />
            {/* Чужой адрес не показывает пустой каркас: опечатка в пути ведёт
                на главный экран, а не в никуда. */}
            <Route path="*" element={<Navigate to="/placement" replace />} />
          </Route>
        </Routes>
      </Refine>
    </BrowserRouter>
  );
}

/**
 * Старый адрес карточки короба — на карточку МЕСТА, тем же номером.
 *
 * Номер в обоих адресах один и тот же (`placeID` CRM), поэтому переадресация
 * честная: человек, открывший ссылку из наряда или переписки, попадает ровно в
 * тот короб, который в ней назван. `replace` — чтобы «назад» вело туда, откуда
 * пришли, а не обратно в редирект.
 */
function BoxToPlace() {
  const { placeId = "" } = useParams();
  return <Navigate to={`/place/${placeId}`} replace />;
}

/**
 * Старый адрес «Подсорта FBW» — на «Подсорт → Расчёт» с ТЕМ ЖЕ отбором.
 *
 * Ключи адреса у экранов одни и те же (`city`, `shelf`, `norm`…), а площадки в
 * старой ссылке нет, и это и есть ВБ — площадка по умолчанию.
 */
function FbwToPodsort() {
  const { search } = useLocation();
  return <Navigate to={`/podsort${search}`} replace />;
}

/** Прежний адрес «Прихода и списания» — на новый, с тем же периодом. */
function BalanceToSummary() {
  const { search } = useLocation();
  return <Navigate to={`/spisanie/summary${search}`} replace />;
}
